import { useQueryClient } from '@tanstack/react-query';
import { getGetDebtorQueryOptions } from 'api';
import { useDebtor } from 'app/Private/Clients/Detail/client-atom';
import { loadDebtorAiSummaryQueryFn } from 'app/Private/Clients/Detail/InfoTab/components/AiSummaryPanel/hooks/use-load-debtor-ai-summary';
import { loadUnhandledClientQueryFn } from 'modules/AtRisk/api/use-load-unhandled-client';
import { useLoadUnhandledClients } from 'modules/AtRisk/api/use-load-unhandled-clients';
import { useEffect } from 'react';
import { useAxiosInstance } from 'shared/hooks/utils';

// MARK: Prefetch a single debtor's at-risk tab data
export function usePrefetchDebtorAtRisk() {
  const queryClient = useQueryClient();
  const axiosInstance = useAxiosInstance();

  return (debtorId: string) => {
    queryClient.prefetchQuery(getGetDebtorQueryOptions(debtorId));
    queryClient.prefetchQuery({
      queryKey: ['unhandledClient', { debtorId }],
      queryFn: () => loadUnhandledClientQueryFn(axiosInstance, { debtorId }),
    });
    queryClient.prefetchQuery({
      queryKey: ['aiSummary', { id: debtorId }],
      queryFn: () => loadDebtorAiSummaryQueryFn(axiosInstance, { id: debtorId }),
    });
  };
}

// MARK: Determine the next debtor to handle
export function useNextDebtorId(currentDebtorId?: string): string | undefined {
  const {
    unhandledClients: { unhandledClients },
  } = useLoadUnhandledClients({});

  if (currentDebtorId === undefined) return unhandledClients[0]?.id;
  return unhandledClients.find(c => c.id !== currentDebtorId)?.id;
}

// MARK: Combined wrapper — drop into any page to prefetch the next debtor
export function usePrefetchNextDebtorAtRisk(currentDebtorId?: string) {
  const nextDebtorId = useNextDebtorId(currentDebtorId);
  const prefetchDebtorAtRisk = usePrefetchDebtorAtRisk();

  useEffect(() => {
    if (nextDebtorId != null) prefetchDebtorAtRisk(nextDebtorId);
    // prefetchDebtorAtRisk is intentionally excluded: it closes over stable refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextDebtorId]);
}

// MARK: Convenience hook for the At Risk tab — resolves current debtor automatically
export function usePrefetchNextDebtorAtRiskFromTab() {
  const debtor = useDebtor();
  usePrefetchNextDebtorAtRisk(String(debtor.id));
}
